// NetcongestieKaartBron.js — de aansluitingskaart van de netbeheerder inlezen.
//
// De netbeheerder publiceert die kaart maandelijks als PDF. Hier wordt hij
// geopend, omgezet naar een afbeelding en naar de server gestuurd. De server
// leest er per statistische sector de kleur uit af.
//
// Waarom het omzetten hier gebeurt en niet op de server: de browser heeft het
// bestand al in handen zodra je het sleept, en kan een PDF openen. De server
// zou daar een apart programma voor nodig hebben.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const KAARTLINK = 'https://www.fluvius.be/nl/nieuws/netcongestiemaatregelen-in-vlaanderen/cijfers-kaarten';

// Op deze resolutie is één beeldpunt ongeveer 46 meter. Daarmee is ook een
// kleine sector nog te meten. Hoger maakt het bestand onnodig zwaar.
const DPI = 400;

// De vier hoeken van het kaartvlak staan in de PDF zelf, in het onderdeel dat
// de kaart aan de aarde koppelt. Ze worden hier rechtstreeks uit de bytes
// gelezen: acht getallen, telkens een breedtegraad gevolgd door een lengtegraad.
function leesHoeken(bytes) {
  let tekst = '';
  for (let i = 0; i < bytes.length; i++) tekst += String.fromCharCode(bytes[i]);
  const m = tekst.match(/\/GPTS\s*\[([^\]]+)\]/);
  if (!m) return null;
  const g = m[1].trim().split(/\s+/).map(Number).filter(Number.isFinite);
  if (g.length < 8) return null;
  const breedten = [g[0], g[2], g[4], g[6]];
  const lengten  = [g[1], g[3], g[5], g[7]];
  return {
    latBoven:  Math.max(...breedten),
    latOnder:  Math.min(...breedten),
    lonLinks:  Math.min(...lengten),
    lonRechts: Math.max(...lengten),
  };
}

// De datum die de netbeheerder aan de kaart meegeeft, zodat je later ziet welke
// versie er in de app zit. Staat die er niet in, dan blijft het veld leeg.
function leesDatum(bytes) {
  let tekst = '';
  for (let i = 0; i < Math.min(bytes.length, 400000); i++) tekst += String.fromCharCode(bytes[i]);
  const m = tekst.match(/\/CreationDate\s*\(D:(\d{4})(\d{2})(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

function dagenGeleden(datum) {
  if (!datum) return null;
  const d = new Date(datum);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function NetcongestieKaartBron({ stijl: s, kleuren: N, stand, stuur }) {
  const [gegevens, setGegevens] = useState(null);
  const [bezig, setBezig]       = useState(false);
  const [stap, setStap]         = useState('');
  const [fout, setFout]         = useState('');
  const veldRef                 = useRef(null);

  const haalOp = useCallback(async () => {
    try { setGegevens(await stand()); } catch (e) { /* de kaart is niet verplicht */ }
  }, [stand]);
  useEffect(() => { haalOp(); }, [haalOp]);

  // Zolang het aflezen op de server bezig is, elke vijf seconden kijken.
  useEffect(() => {
    if (gegevens?.kaart?.stand !== 'bezig') return undefined;
    const t = setInterval(haalOp, 5000);
    return () => clearInterval(t);
  }, [gegevens, haalOp]);

  const verwerk = async (bestand) => {
    if (!bestand) return;
    setFout(''); setBezig(true);
    try {
      setStap('Kaart openen…');
      const bytes = new Uint8Array(await bestand.arrayBuffer());
      const hoeken = leesHoeken(bytes);
      if (!hoeken) {
        throw new Error('In deze PDF staan geen kaartcoördinaten. Gebruik de kaart ' +
          'zoals die op de pagina van de netbeheerder staat, zonder hem eerst te bewerken.');
      }
      const datum = leesDatum(bytes);

      setStap('Kaart omzetten naar een afbeelding…');
      const doc = await pdfjs.getDocument({ data: bytes }).promise;
      const pagina = await doc.getPage(1);
      const beeld = pagina.getViewport({ scale: DPI / 72 });
      const doek = document.createElement('canvas');
      doek.width = Math.round(beeld.width);
      doek.height = Math.round(beeld.height);
      await pagina.render({ canvasContext: doek.getContext('2d'), viewport: beeld }).promise;

      const blob = await new Promise((klaar) => doek.toBlob(klaar, 'image/png'));
      if (!blob) throw new Error('De afbeelding kon niet gemaakt worden.');

      setStap('Naar de server sturen…');
      await stuur(blob, hoeken, datum, bestand.name);
      setStap('De server leest de kaart af. Dit duurt ongeveer een minuut.');
      await haalOp();
    } catch (e) {
      setFout(e.message);
      setStap('');
    }
    setBezig(false);
  };

  const kaart = gegevens?.kaart;
  const dagen = dagenGeleden(kaart?.peildatum || kaart?.geladen_op);
  const verouderd = dagen != null && dagen > 31;
  const kleur = !kaart ? 'mis' : kaart.stand === 'mislukt' ? 'mis' : verouderd ? 'let' : 'ok';

  return (
    <>
      <div style={s.sec}>Kaart van de netbeheerder</div>
      <div style={s.kaart(kleur)}>
        <div style={s.ico(kleur)}>{kleur === 'ok' ? '✓' : kleur === 'let' ? '!' : '–'}</div>
        <div style={s.inh}>
          <div style={s.naam}>Aansluitingskaart netcongestie</div>
          <div style={s.herkomst}>Fluvius · per statistische sector</div>

          <div style={{ fontSize: 13, color: N.ink2, marginTop: 8, lineHeight: 1.5 }}>
            {!kaart && 'Er is nog geen kaart ingelezen. De app gebruikt de vaste aflezing van 26 juni 2026.'}
            {kaart?.stand === 'bezig' && 'De server leest de kaart af. Dit duurt ongeveer een minuut.'}
            {kaart?.stand === 'mislukt' && `Het aflezen is mislukt: ${kaart.melding || 'onbekende oorzaak'}.`}
            {kaart?.stand === 'klaar' && (
              <>
                Kaart van {kaart.peildatum ? String(kaart.peildatum).slice(0, 10) : 'onbekende datum'},
                {' '}{gegevens.sectoren} sectoren afgelezen.
                {verouderd && ' Deze kaart is ouder dan een maand. Kijk op de website van de netbeheerder of er een nieuwere versie staat.'}
              </>
            )}
          </div>

          {bezig && stap && (
            <div style={{ fontSize: 13, color: N.ink3, marginTop: 8 }}>{stap}</div>
          )}
          {fout && (
            <div style={{ fontSize: 13, color: N.red, marginTop: 8, lineHeight: 1.5 }}>{fout}</div>
          )}

        </div>

        {/* Rechts, in dezelfde vorm als bij de andere bronnen. Het bestandsveld
            zit verstopt achter de knop. */}
        <div style={s.akties}>
          <button type="button" disabled={bezig}
            style={{ ...s.knop('eerste'), opacity: bezig ? 0.6 : 1,
                     cursor: bezig ? 'default' : 'pointer' }}
            onClick={() => veldRef.current?.click()}>
            {bezig ? 'Bezig…' : kaart ? 'Nieuwe kaart' : 'Kaart inlezen'}
          </button>
          <input ref={veldRef} type="file" accept="application/pdf" style={{ display: 'none' }}
            onChange={(e) => { verwerk(e.target.files?.[0]); e.target.value = ''; }} />
          <a style={s.bronLink} href={KAARTLINK} target="_blank" rel="noreferrer">
            Kaart downloaden ↓
          </a>
        </div>
      </div>
    </>
  );
}
